import type { resources, common } from '../gads'
import { SlideEnums } from './slides/enums'
import { GadsFieldsRequired } from '../utils/global'
import { PerformanceMode } from '../targets'

export namespace Core {
    export type ID = number

    export interface Sender {
        user_id: number // 416 -- required for generating the correct boringAvatar when no avatar url is set
        name: string // Agency name e.g. "Powered By Search"
        email: string // "hello@poweredbysearch.com"
        logo: string // "https://logo.clearbit.com/poweredbysearch.com"
        website: string // "poweredbysearch.com"
        telephone: string // "+44 (0) 7359 482 209"
        address_line1: string // "28 Tower Bridge Road"
        address_line2: string // "Southwark"
        city: string // "London"
        post_code: string // "SE1 5AB"
        avatar_filename: string // "cc148963/tintin.png"
        avatar_url: string // "https://shubox-bucket.s3-eu-west-2.amazonaws.com/www-app-opteo-com/cc148963/tintin.png"
    }

    export interface Recipient {
        name: string // Company name e.g. "Deliveroo"
        website: string // "deliveroo.com"
        logo: string // "https://shubox-bucket.s3-eu-west-2.amazonaws.com/blah/logo"
    }

    export interface ReportTheme {
        primary_colour: string
        accent_colour: string
        domain_colour: string
        typeface: ReportTypeface
    }

    export type ReportTypeface = 'professional' | 'colourful' | 'modern' | 'serif' | 'traditional'

    export interface ReportPreferences {
        template_id: TemplateID
        typeface: ReportTypeface
    }

    export interface Target {
        top: number // Spend: 20000, CPA: 10
        bottom: number // Spend: 25000, CPA: 5
    }

    export interface GetNewReportInfo {
        group_id: number
        user_id: number
        domain_id: number
        from_date?: string
        to_date?: string
    }

    export interface NewReportInfo {
        sender: Sender
        recipient: Recipient
        spend_target: Target
        cpa_target: Target
        roas_target: Target
        conversion_types: Array<ConversionType>
        theme: ReportTheme
        schedules: ReportSchedule[]
        from_date: string
        to_date: string
        performance_mode: PerformanceMode
    }

    export interface ConversionType {
        name: string
        selected: boolean
        all_conversions: number
        all_conversions_value: number
        resource_name: string
    }

    export interface CreateReportOptions extends NewReportInfo {
        title: string // "Adwords Report (May 2019)"
        theme: ReportTheme
        template_id: TemplateID
        currency: string // "£"|"$"|"RON"
    }

    export interface LogoColours {
        primary: string // Hex codes
        secondary: string
    }

    export interface ReportsListItem {
        user_id: number
        report_id: ID
        title: string
        last_exported_ts: string | null
        last_updated_ts: string
        created_ts: string
        deleted_ts: string | null
        archived: boolean
        from_date: string // "2019-03-01"
        to_date: string // "2019-03-01"
        date_range: string // "December 1st - February 28th"
        name: string // "Will Cooter" -> user who created the report
        profile_url: string | undefined // AWS S3 URL of the users profile image
    }

    export type ReportsList = Array<ReportsListItem>

    export interface TopPerformingAd {
        resource_name: string
        ad: resources.Ad
        root_domain: string
        clicks: number
        conversions: number
        conversion_rate: number
        impressions: number
        extensions?: {
            // enums.ExtensionType as string
            [extention_type: string]: []
        }
        search_term: string
    }

    export interface ReportSlide {
        slide_id: string
        /* Page number for the slide (starts at 1) */
        page_no: number
        /* If slide is currently active, defaults to true */
        active: boolean
        /* If the account lacks sufficient data for the slide, this is true */
        invalid: boolean
        /* if `invalid = true`, this is a human-readable justification */
        invalid_reason?: string
        /* If slide text is auto generated, and not edited by the user */
        default: boolean
        /* The slide type enum */
        type: SlideEnums.SlideType
        /* Slide category */
        category: SlideEnums.SlideCategory
        /* Text title of the slide */
        title?: string
        /* Main text included in the slide */
        text?: string
        /* Copy of the default text (saved so the user can revert back) */
        readonly default_text?: string
        /* Chart config for the slide */
        chart?: ReportChart | ReportTargetChart
        /* Data for a table on a slide */
        table?: ReportTable
        /* Full screen image (cover slide) */
        image?: string
        /* Ad with metrics */
        ad?: {
            resource_name: string
            root_domain: string
            impressions: string
            clicks: string
            conversions: string
            conversion_rate: string
            extensions:
                | undefined
                | {
                      // enums.ExtensionType as string
                      [extention_type: string]: []
                  }
            search_term: string
        } & resources.Ad
        /* Custom component to render with props */
        custom?: {
            component: string
            props: Record<string, unknown>
        }
        /* The actual number of the slide in created order */
        slide_order: number
    }

    export interface ReportChart {
        type: 'summary'
        title?: string
        total?: string | number
        delta?: string
        points: Array<{ x: number; y: number }>
        increase: boolean
        data_type: 'number' | 'money' | 'percentage' | 'roas'
        currency_code: string
    }

    export interface ReportTargetChart {
        type: 'target'
        title: string
        total: string | number
        target: Target
        status:
            | ('On Target' | 'Above Target' | 'Below Target') // Cost | ROAS
            | ('On Target' | 'Over Target' | 'Under Target') // CPA
        points: Array<{ x: number; y: number }>
        chart_type: 'spend' | 'cpa' | 'roas'
        data_type: 'number' | 'money' | 'percentage' | 'roas'
        currency_code: string
    }

    export interface ReportTable {
        columns: Array<{
            name: string // "Impressions"
            key: string // "impressions"
        }>
        data: Array<{ [field: string]: number | string }> // [{ impressions: 3 }]
    }

    export interface Report extends NewReportInfo {
        report_id: ID
        title: string
        uuid: string
        theme: ReportTheme
        template_id: TemplateID
        date: string
        slides: Array<ReportSlide>
        insights: ReportInsights
        viewed_insights: boolean
        auto_gen: boolean
        work_summary: WorkSummaryRow[]
    }

    export interface ReportWithUserDetails extends Report {
        user: {
            username: string
            email: string
            logo: string | undefined
        }
    }

    export interface ReportInsights {
        [slide_type: string]: InsightSentencesByType
    }

    export enum InsightType {
        Generic = 'generic',
        Insight = 'insight',
    }

    export interface InsightSentencesByType {
        // "insight_type" here corresponds to InsightType enum
        [insight_type: string]: string[]
    }

    export interface GetReportBuffer {
        report_pdf_id: number
        hash: string
        pdf_buffer: Buffer
    }

    export interface ReportDownloadUrl {
        download_url: string
    }

    export interface CopyMetric {
        // The value for the selected period
        current: number
        // The value for the previous period
        previous: number
        // Percent change from prev to selected period (absolute)
        delta: number
        // True when the metrics has gone from 0 to x, so the %delta doesn't make sense
        delta_is_infinite: boolean
        // Percent change from prev to current (non abs, signed)
        change: number
        // If the prev to current change is an increase
        increase: boolean
        // Per day values, used for the charts
        values: Array<{
            x: number
            y: number
        }>
        // Average for current period (only required for some metrics, default=0)
        average: number
    }

    export interface CopyMetrics {
        [name: string]: CopyMetric
        conversions: CopyMetric
        conversions_value: CopyMetric
        cpa: CopyMetric
        roas: CopyMetric
        cost: CopyMetric
        impressions: CopyMetric
        clicks: CopyMetric
        cpc: CopyMetric
        conversion_rate: CopyMetric
        ctr: CopyMetric
        qs: CopyMetric
    }

    export interface TopPerformingSegments {
        real_name: string
        segment_label: string
        conversions: number
        conversions_value: number
        cost: number
        cpa: number
        roas: number
        difference: number
        cpa_target?: number
        is_below_cpa?: boolean // This is really is below target
        roas_target?: number
    }

    export interface ReportDates {
        from_date: string // "2019-04-01"
        to_date: string // "2019-04-31"
        last_period_from_date: string // "2019-03-01"
        last_period_to_date: string // "2019-03-31"
        date_range: string // "April 1st → April 31st",
        date_range_with_year: string // "April 1st 2020 → April 31st 2020",
        prev_date_range: string // "May 1st → May 28th"
        // Historical ranges doesn't include the current from_date, to_date
        periods: Array<{ from_date: string; to_date: string }>
    }

    export interface ReportAlerts {
        delta: {
            alert_id: number
            body: any
        }
    }

    export interface TopNonBrandingKeyword {
        conversions: number
        conversions_value: number
        keyword: {
            text: string
        }
        resource_name: string
    }

    export interface ReportSchedule {
        interval_amount: ReportScheduleInterval
        interval_unit: 'months'
        day: ReportScheduleDay
        active: boolean

        // These only exist after the schedule has been created
        schedule_id?: number
        title?: string
        next_run_ts?: string
    }

    export enum ReportScheduleInterval {
        Monthly = 1,
        Bimonthly = 2,
    }

    export type ReportScheduleDay = 3

    export interface ScheduleWithReportInfo extends ReportSchedule {
        schedule_id: number
        domain_id: number
        user_id: number
        title: string // "Adwords Report (May 2019)"
        from_date: string // "2019-04-01"
        to_date: string // "2019-05-01"
        info: NewReportInfo
    }

    export interface BaseReportSlide {
        slide_name: string
        slide_id: number
        selected: boolean
    }

    export interface BaseReportCategory {
        category_name: string
        category_id: number
        selected: boolean
        slide_ids: number[]
        slides: BaseReportSlide[]
    }

    export type BaseReportTemplate = BaseReportCategory[]

    export type TemplateID = number

    export interface ReportTemplate {
        template_id: TemplateID
        template_name: string
        slide_types: SlideEnums.SlideType[]
        slide_categories: SlideEnums.SlideCategory[]
        slide_strings: string[]
        slide_count: number
    }

    export interface AccountInfo {
        ga_account_name: string
        opt_display_name: string
        opt_colour: string
        logo_filename: string
    }

    export interface WorkSummaryRow {
        name: string
        number: number
        time: number
        active: boolean
    }

    export interface CustomerMetrics {
        metrics: GadsFieldsRequired<
            common.Metrics,
            | 'cost_micros'
            | 'impressions'
            | 'clicks'
            | 'average_cpc'
            | 'ctr'
            | 'conversions'
            | 'conversions_value'
        > & { qs: number }
        segments: GadsFieldsRequired<common.Segments, 'date'>
    }

    export interface GroupedRandomReportDetails {
        [group_id: number]: RandomReportDetails[]
    }

    export interface RandomReportDetails {
        uuid: string
        group_id: number
        user_id: number
        hash: string
        title: string
    }
}
